import { FC, useEffect, useState } from 'react'
import { Bar } from 'react-chartjs-2/dist'
import { useTranslation } from 'react-i18next'
import { Checkbox } from 'antd'
import { roundUp } from '../../utils'
import style from './style.m.less'

const useChartTranslations = () => {
    const { t } = useTranslation()

    return {
        certificatesOfOrigin: t('main.originCert'),
        externalInstruments: t('main.otherGreenTools'),
        greenContracts: t('main.greenDocs'),
        splitCertificates: t('main.splitCertificates'),
    }
}

const data = (
    graphData,
    toolType,
    translations,
    hiddenIndices: Set<number>,
    withSplitCerts: boolean,
    greenToolsStats?: { year: number; volume: number }[],
    greenToolsStatsExpired?: { year: number; volume: number }[],
) => {
    const labels = []
    const certSet = [
        {
            label: translations.certificatesOfOrigin,
            data: [],
            backgroundColor: '#52D2A0',
            barThickness: 56,
        },
    ]
    const othersSet = [
        {
            label: translations.externalInstruments,
            data: [],
            backgroundColor: '#F4FFB8',
            barThickness: 56,
        },
    ]
    const contractsSet = [
        {
            label: translations.greenContracts,
            data: [],
            backgroundColor: '#EFDBFF',
            barThickness: 56,
        },
    ]

    const datasets =
        toolType === 'Истек срок действия'
            ? [...othersSet, ...certSet]
            : [...othersSet, ...contractsSet, ...certSet]

    const filteredGraphData =
        toolType === 'Истек срок действия' && greenToolsStatsExpired
            ? graphData.filter((item) => greenToolsStatsExpired.some((s) => s.year === item.year))
            : graphData

    filteredGraphData.forEach((item) => {
        labels.push(item.year)
        if (toolType === 'Выпущенные') {
            const certVolume = !withSplitCerts
                ? Math.round(
                      (greenToolsStats?.find((s) => s.year === item.year)?.volume ?? 0) / 1000,
                  )
                : Math.round(item.certificatesIssueVolume / 1000)
            datasets[0].data.push(
                hiddenIndices.has(0) ? 0 : Math.round(item.extInstrumentsTotalVolume / 1000),
            )
            datasets[1].data.push(
                hiddenIndices.has(1) ? 0 : Math.round(item.contractTotalVolume / 1000),
            )
            datasets[2].data.push(hiddenIndices.has(2) ? 0 : certVolume)
        } else if (toolType === 'Погашенные') {
            datasets[0].data.push(
                hiddenIndices.has(0) ? 0 : Math.round(item.extInstrumentsUsedVolume / 1000),
            )
            datasets[1].data.push(
                hiddenIndices.has(1) ? 0 : Math.round(item.contractUsedVolume / 1000),
            )
            datasets[2].data.push(
                hiddenIndices.has(2) ? 0 : Math.round(item.certificatesUsedVolume / 1000),
            )
        } else if (toolType === 'Истек срок действия') {
            datasets[0].data.push(
                hiddenIndices.has(0)
                    ? 0
                    : item?.extInstrumentsExpiredVolume
                    ? Math.round(item.extInstrumentsExpiredVolume / 1000)
                    : 0,
            )
            const expiredCertVolume = greenToolsStatsExpired
                ? Math.round(
                      (greenToolsStatsExpired.find((s) => s.year === item.year)?.volume ?? 0) /
                          1000,
                  )
                : item?.certificatesExpiredVolume
                ? Math.round(item.certificatesExpiredVolume / 1000)
                : 0
            datasets[1].data.push(hiddenIndices.has(1) ? 0 : expiredCertVolume)
        }
    })
    return {
        labels,
        datasets,
    }
}

const options = (graphData: any, toolType: any, hiddenIndices: Set<number>) => {
    const values_ = []
    const sums = []
    let i = 0
    graphData.forEach((item) => {
        values_.push([])
        let sum = 0
        if (toolType === 'Выпущенные') {
            const v0 = hiddenIndices.has(0) ? 0 : item.extInstrumentsTotalVolume
            const v1 = hiddenIndices.has(1) ? 0 : item.contractTotalVolume
            const v2 = hiddenIndices.has(2) ? 0 : item.certificatesIssueVolume
            values_[i].push(v0, v1, v2)
            sum += v0 + v1 + v2
        } else if (toolType === 'Погашенные') {
            const v0 = hiddenIndices.has(0) ? 0 : item.extInstrumentsUsedVolume
            const v1 = hiddenIndices.has(1) ? 0 : item.contractUsedVolume
            const v2 = hiddenIndices.has(2) ? 0 : item.certificatesUsedVolume
            values_[i].push(v0, v1, v2)
            sum += v0 + v1 + v2
        } else if (toolType === 'Истек срок действия') {
            const v0 = hiddenIndices.has(0)
                ? 0
                : item?.extInstrumentsExpiredVolume
                ? item.extInstrumentsExpiredVolume
                : 0
            const v1 = hiddenIndices.has(1)
                ? 0
                : item?.certificatesExpiredVolume
                ? item.certificatesExpiredVolume
                : 0
            values_[i].push(v0, v1)
            sum += v0 + v1
        }
        sums.push(sum)
        i++
    })

    let sum = 0
    let max_sum_i = 0
    for (let i = 0; i < sums.length; i++) {
        if (sums[i] > sum) {
            sum = sums[i]
            max_sum_i = i
        }
    }

    const values = values_[max_sum_i]
    const preparedValues = values?.map((item) => Math.round(item / 1000)) || []
    const maxValue = preparedValues.length
        ? preparedValues.reduce((partialSum, value) => partialSum + value, 0)
        : 100000
    const cleanMaxValue = maxValue !== 0 ? roundUp(maxValue, 1000) : 100000
    const stepSize = Math.round(cleanMaxValue / 5)
    const max = cleanMaxValue + stepSize

    return {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
        },
        scales: {
            x: {
                stacked: true,
                grid: {
                    display: false,
                },
                ticks: {
                    color: '#8C8C8C',
                    font: {
                        size: 14,
                        weight: '400',
                    },
                },
            },
            y: {
                stacked: true,
                position: 'right' as const,
                grid: {
                    color: '#E6F1FF',
                },
                min: 0,
                max,
                ticks: {
                    color: '#8C8C8C',
                    font: {
                        size: 14,
                        weight: '400',
                    },
                    stepSize,
                },
            },
        },
    }
}

interface IStackedBarChart {
    toolType: string
    graphData: any
    withSplitCerts: boolean
    onCheckboxChange: (checked: boolean) => void
    greenToolsStats?: { year: number; volume: number }[]
    greenToolsStatsExpired?: { year: number; volume: number }[]
}

const StackedBarChart: FC<IStackedBarChart> = ({
    toolType,
    graphData,
    withSplitCerts,
    onCheckboxChange,
    greenToolsStats,
    greenToolsStatsExpired,
}) => {
    const translations = useChartTranslations()
    const [hiddenDatasets, setHiddenDatasets] = useState<Set<number>>(new Set())

    useEffect(() => {
        setHiddenDatasets(new Set())
    }, [toolType, withSplitCerts])

    const legendItems =
        toolType === 'Истек срок действия'
            ? [
                  { label: translations.certificatesOfOrigin, color: '#52D2A0', index: 1 },
                  { label: translations.externalInstruments, color: '#F4FFB8', index: 0 },
              ]
            : [
                  { label: translations.certificatesOfOrigin, color: '#52D2A0', index: 2 },
                  { label: translations.greenContracts, color: '#EFDBFF', index: 1 },
                  { label: translations.externalInstruments, color: '#F4FFB8', index: 0 },
              ]

    return graphData ? (
        <div className={style.chartViewport}>
            <div style={{ flex: 1 }}>
                <Bar
                    data={data(
                        graphData,
                        toolType,
                        translations,
                        hiddenDatasets,
                        withSplitCerts,
                        greenToolsStats,
                        greenToolsStatsExpired,
                    )}
                    options={options(graphData, toolType, hiddenDatasets)}
                />
            </div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    paddingLeft: 24,
                    gap: 16,
                    minWidth: 220,
                }}
            >
                {legendItems.map((item) => (
                    <div
                        key={item.index}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            cursor: 'pointer',
                            opacity: hiddenDatasets.has(item.index) ? 0.4 : 1,
                        }}
                        onClick={() => {
                            setHiddenDatasets((prev) => {
                                const next = new Set(prev)
                                if (next.has(item.index)) {
                                    next.delete(item.index)
                                } else {
                                    next.add(item.index)
                                }
                                return next
                            })
                        }}
                    >
                        <div
                            style={{
                                width: 15,
                                height: 15,
                                backgroundColor: item.color,
                                flexShrink: 0,
                            }}
                        />
                        <span style={{ fontSize: 14, color: '#595959' }}>{item.label}</span>
                    </div>
                ))}
                {toolType === 'Выпущенные' && (
                    <Checkbox
                        checked={withSplitCerts}
                        onChange={(e) => onCheckboxChange(e.target.checked)}
                        className="grayCheckbox"
                    >
                        {translations.splitCertificates}
                    </Checkbox>
                )}
            </div>
        </div>
    ) : (
        <></>
    )
}

export default StackedBarChart
